import React, {
	useEffect,
	useState,
} from 'react';
import {
	Alert,
	Card,
	Col,
	Row,
} from 'antd';
import {
	BankAccountTypes,
	CustomFieldParentTypes,
	VesselOwnershipTypes,
} from '@shared/utils/constants';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import { BankAccountTabs } from '@client/screens/fleet/VesselDetailsScreen/components/BankAccountTabs';
import VesselOurBankAccounts from '@client/screens/fleet/VesselDetailsScreen/components/VesselOurBankAccounts';
import useVesselBankAccountHandlers from '@client/screens/fleet/VesselDetailsScreen/hooks/useVesselBankAccountHandlers';
import { createBankAccount } from '@client/lib/api';
import styles from '@client/screens/fleet/VesselDetailsScreen/VesselDetailsScreen.module.css';
import CustomFields from '@client/screens/management/banking/CustomFields/CustomFields';

type Props = {
	vessel: GetVesselDetailsResponse;
	refreshVessel: () => void;
};

export const VesselBankingTab = ({ vessel, refreshVessel }: Props) => {
	const [activeTcInTab, setActiveTcInTab] = useState<number | undefined>(undefined);
	const isTcIn = vessel.ownershipType === VesselOwnershipTypes.TC_IN;
	const {
		onSaveBankAccount,
		onDeleteBankAccount,
	} = useVesselBankAccountHandlers(vessel.id, refreshVessel);

	useEffect(() => {
		if (!vessel || !isTcIn) {
			return;
		}

		if (
			!activeTcInTab ||
			!vessel.tcInBankAccounts.some((account) => account.id === activeTcInTab)
		) {
			setActiveTcInTab(vessel.tcInBankAccounts.length > 0 ?
				vessel.tcInBankAccounts[0].id :
				undefined);
		}
	}, [activeTcInTab, isTcIn, vessel]);

	const onAddTcInBankAccount = async () => {
		const newAccount = await createBankAccount(BankAccountTypes.TC_IN, vessel.id);
		await refreshVessel();

		return newAccount;
	};

	return (
		<Row gutter={[16, 16]}>
			{isTcIn && (
				<Col span={12}>
					<Card
						bordered
						title="Vessel Owner's Banking Details"
						className={styles.vesselDetailsCard}
					>
						{vessel.tcInBankAccounts?.length > 0 && (
							<BankAccountTabs
								activeBankAccountTab={activeTcInTab}
								setActiveBankAccountTab={setActiveTcInTab}
								vesselOwner
								bankAccounts={vessel.tcInBankAccounts}
								deleteBankAccount={onDeleteBankAccount}
								addBankAccount={onAddTcInBankAccount}
								saveBankAccount={onSaveBankAccount}
								allowCreate={false}
								showInvoiceCompanyName
							/>
						)}
						{activeTcInTab && (
							<CustomFields
								parentId={Number(activeTcInTab)}
								parentType={CustomFieldParentTypes.BANK}
							/>
						)}
					</Card>
				</Col>
			)}
			<Col span={12}>
				{isTcIn ? (
					<Card
						bordered
						title="Your Banking Details"
						className={styles.vesselDetailsCard}
					>
						<Alert message="Bank accounts for TC-In vessels are managed under Company Settings > Comp. & bank" />
					</Card>
				) : (
					<VesselOurBankAccounts
						vesselId={vessel.id}
						bankAccounts={vessel.ourBankAccounts ?? []}
						onChange={refreshVessel}
						className={styles.vesselDetailsCard}
					/>
				)}
			</Col>
		</Row>
	);
};
